import { IssueReportsSelectOptionsContext } from './types'
import { IssueReportType, IssueReportTypeItem } from '@cims-online/shared'
import { join, split, toLabeledValues } from './utils'
import { DefaultOptionType } from 'antd/lib/select'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  useAsphaltIssueReportOptionsQuery,
  useConcreteTestIssueReportOptionsQuery,
  useConcreteTestSetIssueReportOptionsQuery,
  useControlPointIssueReportOptionsQuery,
  useInspectionIssueReportOptionsQuery,
  useLotIssueReportOptionsQuery,
  usePunchListIssueReportOptionsQuery,
} from 'schema'
import { useAnyTrue } from 'hooks/useAnyTrue'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<IssueReportsSelectOptionsContext>(
    'IssueReportsSelectOptionsContext'
  )

export const useIssueReportsSelectOptionsContext = useContextFn

const IssueReportsSelectOptionsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const options = {
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  }
  const asphalts = useAsphaltIssueReportOptionsQuery(options)
  const inspections = useInspectionIssueReportOptionsQuery(options)
  const concreteTests = useConcreteTestIssueReportOptionsQuery(options)
  const concreteTestSets = useConcreteTestSetIssueReportOptionsQuery(options)
  const controlPoints = useControlPointIssueReportOptionsQuery(options)
  const lots = useLotIssueReportOptionsQuery(options)
  const punchLists = usePunchListIssueReportOptionsQuery(options)

  const [loading] = useAnyTrue([
    asphalts.loading,
    inspections.loading,
    concreteTests.loading,
    concreteTestSets.loading,
    controlPoints.loading,
    lots.loading,
  ])

  const optionsMap = new Map<
    IssueReportTypeItem['classType'],
    DefaultOptionType[]
  >([
    [
      IssueReportType.AsphaltLot.classType,
      useOptions({
        items: asphalts.data?.asphaltLots?.records,
        label: 'asphaltLotId',
        value: join,
        tooltip: () => IssueReportType.AsphaltLot.type,
        sort: 'asphaltLotId',
      }),
    ],
    [
      IssueReportType.DailyInspectionInspection.classType,
      useOptions({
        items: inspections.data?.dailyInspectionInspections?.records,
        label: 'inspectionId',
        value: join,
        tooltip: () => IssueReportType.DailyInspectionInspection.type,
        sort: 'inspectionId',
      }),
    ],
    [
      IssueReportType.ConcreteTest.classType,
      useOptions({
        items: concreteTests.data?.concreteTests?.records,
        label: 'fieldNumber',
        value: join,
        tooltip: () => IssueReportType.ConcreteTest.type,
        sort: 'fieldNumber',
      }),
    ],
    [
      IssueReportType.ConcreteTestSet.classType,
      useOptions({
        items: concreteTestSets.data?.concreteTestSets?.records,
        label: 'testSetId',
        value: join,
        tooltip: () => IssueReportType.ConcreteTestSet.type,
        sort: 'testSetId',
      }),
    ],
    [
      IssueReportType.DailyInspectionControlPoint.classType,
      useOptions({
        items: controlPoints.data?.dailyInspectionControlPoints?.records,
        label: 'controlPointId',
        value: join,
        tooltip: () => IssueReportType.DailyInspectionControlPoint.type,
        sort: 'controlPointId',
      }),
    ],
    [
      IssueReportType.DensityLot.classType,
      useOptions({
        items: lots.data?.lots?.records,
        label: 'lotId',
        value: join,
        tooltip: () => IssueReportType.DensityLot.type,
        sort: 'lotId',
      }),
    ],
    [
      IssueReportType.DailyInspectionPunchList.classType,
      useOptions({
        items: punchLists.data?.dailyInspectionPunchLists?.records,
        label: 'punchListId',
        value: join,
        tooltip: () => IssueReportType.DailyInspectionPunchList.type,
        sort: 'punchListId',
      }),
    ],
  ])

  return (
    <Provider
      value={{
        selectProps: {
          mode: 'multiple',
          options: useOptions({
            items: IssueReportType.values.reduce(
              (types, { classType, type }) => {
                if (types.some((type) => type.classType === classType))
                  return types
                types.push({ classType, type })
                return types
              },
              Array<Pick<IssueReportTypeItem, 'classType' | 'type'>>()
            ),
            type: 'custom',
            custom: ({ classType, type }) => ({
              value: classType,
              label: type,
              options: optionsMap.get(classType) as DefaultOptionType[],
            }),
            sort: 'type',
          }),
          loading,
        },
        fromLabeledValue: (value) => {
          if (!value) return undefined
          return split(value.value)
        },
        fromLabeledValues: (values) =>
          (values ?? []).map(({ value }) => split(value)),
        toLabeledValue: (record) =>
          record ? toLabeledValues([record])[0] : undefined,
        toLabeledValues: (records) => toLabeledValues(records),
      }}
    >
      {children}
    </Provider>
  )
}

export default IssueReportsSelectOptionsProvider
